import { first } from 'rxjs';
import { Action } from 'typescript-fsa';
import { filter, map, takeUntil, catchError, mergeMap, withLatestFrom } from 'rxjs/operators';
import { NetworkStatus } from '@cbridge-utils/network';
import { IEpic, isTypeOfAndGetPayload } from '@cbridge-utils/redux-observable';
import { IGetSelectorStateType } from '@cbridge-utils/reselect';
import {
  DetailsSidebarAction, SidebarOpenMode, getDetailsFromLocationState,
} from '@cbridge-packages/details-sidebar';
import { locationStateSelector } from '@cbridge-packages/routing';
import { PageContextAction } from '../pageContext.action';
import { INetworkStatusAwareSelector } from '../pageContext.interface';

type IState =
  & IGetSelectorStateType<INetworkStatusAwareSelector>
  & IGetSelectorStateType<typeof locationStateSelector>;

export const initPageContextEpic: IEpic<IState> = (action$, state$) => action$.pipe(
  isTypeOfAndGetPayload(PageContextAction.init.started),
  withLatestFrom(state$),
  mergeMap(([params, state]) => {
    const { pageId: initialPageId, resourceId, getNetworkStatus } = params;

    // Get SidebarOpenMode from location state to open Details Sidebar in correct mode
    const { mode, type } = getDetailsFromLocationState(locationStateSelector(state));

    if (!resourceId) {
      const actions: Action<unknown>[] = [
        PageContextAction.init.done({ params }),
      ];
      // Create mode can be set only from current page, so we have to keep the Sidebar opened on a new route.
      // In all other cases Sidebar from the previous route is irrelevant, so we have to close it.
      if (mode === SidebarOpenMode.Create) {
        actions.unshift(DetailsSidebarAction.setState({ mode, type }));
      } else {
        actions.unshift(DetailsSidebarAction.destroy());
      }
      return actions;
    }

    return state$.pipe(
      map(getNetworkStatus),
      first(networkStatus => {
        return networkStatus === NetworkStatus.Done || networkStatus === NetworkStatus.Failed;
      }),
      mergeMap((networkStatus) => {
        if (networkStatus === NetworkStatus.Failed) {
          return [
            PageContextAction.init.failed({ params, error: null }),
          ];
        }
        // Decode resource ID to handle encoded special characters (like # in symbol names)
        const decodedResourceId = decodeURIComponent(resourceId);
        const parsedResourceId = Number(decodedResourceId);
        const id = Number.isNaN(parsedResourceId)
          ? decodedResourceId
          : parsedResourceId;

        return [
          mode === SidebarOpenMode.Edit
            ? DetailsSidebarAction.setState({ id, mode, type })
            : DetailsSidebarAction.openDefault({ id }),
          PageContextAction.init.done({ params }),
        ];
      }),
      takeUntil(
        action$.pipe(
          isTypeOfAndGetPayload(PageContextAction.destroy),
          filter(({ pageId }) => pageId === initialPageId),
        ),
      ),
      catchError(() => {
        return [
          PageContextAction.init.failed({ params, error: null }),
        ];
      }),
    );
  }),
);
